import { Input, Option, Select } from "@material-tailwind/react";
import GalleryPicker from "components/GalleryPicker";
import useObject from "hooks/useObject";
import React, { useEffect, useState } from "react";
import resolveImgUrl from "utils/resolveImgUrl";
import AspectRatioDiv from "components/utils/AspectRatioDiv";
import AdminNavItem from "components/AdminNavItem";
import { Blog, Businesses, navItem, WelcomePage } from "utils/types";
import BlogPicker from "components/ItemPicker";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import Spinner from "components/utils/Spinner";
import useMetaManager from "hooks/useMetaManager";
import useAlert from "hooks/useAlert";
import { BubbleMenu, EditorContent, Mark, useEditor } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import History from "@tiptap/extension-history";
import { Highlight } from "components/utils/Highlight";
import userImg from "assets/images/user.svg";
import ItemList from "components/ItemList";
import mapDataFactory from "utils/mapDataFactory";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "utils/axios";
import BlogsSiteSection from "components/BlogsSiteSection";
import EventsSiteSection from "components/EventsSiteSection";
import JobPostSiteSection from "components/JobPostSiteSection";

export interface SiteData {
  navLogo: string;
  headerLogo: string;
  title: string;
  bannerImg: string[];
}

const Site = () => {
  const { setAlert } = useAlert();

  const axiosPrivate = useAxiosPrivate();

  const queryClient = useQueryClient();
  // Welcome Page
  const {
    data: welcomePageMeta,
    intentData: intentWelcomePageMeta,
    loaded: welcomePageLoading,
    isPosting: welcomePageIsPosting,
  } = useMetaManager<WelcomePage.Type>({
    getUrl: "/site/welcome-page",
    postUrl: "/site/welcome-page",
    initialData: {
      heroText: "Banner Text",
      firstSection: {
        title: "First section",
        description: "write your description here",
      },
      testimonySection: {
        title: "Testimony Section",
        description: "write your description here",
      },
      secondSection: {
        title: "Items Display Section",
        description: "write your description here",
      },
      firstSectionItems: Array(3).fill({
        iconType: "regular",
        icon: "fa fa-user",
        title: "title",
        description: "description",
      }),
      testimonySectionItems: Array(3).fill({
        description: "description",
        image: "https://placehold.co/200x200?text=Hello+World",
        name: "name",
        username: "username",
      }),
    },
  });

  return (
    <div className="flex flex-col relative gap-6">
      <div className="shadow-sm bg-white rounded-md p-4 relative flex flex-col gap-2">
        <h2 className="text-2xl text-gray-700">Welcome Page</h2>
        <div className="absolute top-4 right-4 shadow-sm grid place-content-center text-2xl" title="changes saved">
            { (welcomePageIsPosting) ? 
                <i className="fas fa-cloud-arrow-up text-blue-500 "></i> 
              : <i className="fas fa-cloud text-gray-500"></i>
            }
          </div>
        <hr className="my-2" />
        <div className="flex flex-col gap-4">
          {welcomePageLoading ? (
            <Spinner />
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <h3 className="text-xl text-gray-700">Banner Text</h3>
                <TipTap
                  text={welcomePageMeta.heroText}
                  className="banner-text"
                  onUpdate={(html) =>
                    intentWelcomePageMeta((prev) => {
                      prev.heroText = html;
                      return prev;
                    }, "heroText")
                  }
                />
              </div>

              <hr className="mt-2" />

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl text-gray-700">First Section</h3>
                  <span>/</span>
                  <h4 className="text-xl font-light text-gray-600">Title</h4>
                </div>
                <TipTap
                  text={welcomePageMeta.firstSection?.title}
                  onUpdate={(html) =>
                    intentWelcomePageMeta((prev) => {
                      prev.firstSection!.title = html;
                      return prev;
                    }, "firstSection")
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl text-gray-700">First Section</h3>
                  <span>/</span>
                  <h4 className="text-xl font-light text-gray-600">
                    Description
                  </h4>
                </div>
                <TipTap
                  text={welcomePageMeta.firstSection?.description}
                  onUpdate={(html) =>
                    intentWelcomePageMeta((prev) => {
                      prev.firstSection!.description = html;
                      return prev;
                    }, "firstSection")
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl text-gray-700">First Section</h3>
                  <span>/</span>
                  <h4 className="text-xl font-light text-gray-600">Items</h4>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
                  {welcomePageMeta.firstSectionItems?.map((el, i) => (
                    <div
                      key={i}
                      className="border border-gray-400 w-full flex flex-col gap-3 p-2"
                    >
                      <div>
                        <h4 className="text-gray-600">Icon</h4>
                        <div className="flex gap-2">
                          <div className="w-10 h-10 grid place-content-center border border-gray-400 text-xl">
                            <i
                              className={
                                (el.iconType === "regular" ? "far" : "fas") +
                                " fa-" +
                                el.icon
                              }
                            />
                          </div>
                          <div className="grow bg-blue-200 border-l-2 border-blue-500 px-2 text-sm  text-gray-700 flex items-center gap-1">
                            <span>Click here to get icons for </span>
                            <a
                              className="text-blue-500"
                              href="https://fontawesome.com/search?o=r&m=free&s=solid"
                              target="_blank"
                            >
                              solid
                            </a>
                            <span>or</span>
                            <a
                              className="text-blue-500"
                              href="https://fontawesome.com/search?o=r&m=free&s=regular"
                              target="_blank"
                            >
                              regular
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 override-material-tailwind-min-width">
                        <Select
                          value={el.iconType}
                          label="Icon Type"
                          onChange={(e) =>
                            intentWelcomePageMeta((prev) => {
                              prev.firstSectionItems![i].iconType = e as
                                | "regular"
                                | "solid";
                              return prev;
                            }, "firstSectionItems")
                          }
                        >
                          <Option value="regular">Regular</Option>
                          <Option value="solid">solid</Option>
                        </Select>
                        <Input
                          value={el.icon}
                          className="min-w-0"
                          label="Icon Text"
                          onChange={(e) =>
                            intentWelcomePageMeta((prev) => {
                              prev.firstSectionItems![i].icon = e.target.value;
                              return prev;
                            }, "firstSectionItems")
                          }
                        />
                      </div>
                      <div>
                        <h4 className="text-gray-600">Title</h4>
                        <TipTap
                          className="!w-full"
                          text={el.title}
                          onUpdate={(html) =>
                            intentWelcomePageMeta((prev) => {
                              prev.firstSectionItems![i].title = html;
                              return prev;
                            }, "firstSectionItems")
                          }
                        />
                      </div>
                      <div>
                        <h4 className="text-gray-600">Description</h4>
                        <TipTap
                          className="!w-full"
                          text={el.description}
                          onUpdate={(html) =>
                            intentWelcomePageMeta((prev) => {
                              prev.firstSectionItems![i].description = html;
                              return prev;
                            }, "firstSectionItems")
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="mt-2" />

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl text-gray-700">Second Section</h3>
                  <span>/</span>
                  <h4 className="text-xl font-light text-gray-600">Title</h4>
                </div>
                <TipTap
                  text={welcomePageMeta.secondSection?.title}
                  onUpdate={(html) =>
                    intentWelcomePageMeta((prev) => {
                      prev.secondSection!.title = html;
                      return prev;
                    }, "secondSection")
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl text-gray-700">Second Section</h3>
                  <span>/</span>
                  <h4 className="text-xl font-light text-gray-600">
                    Description
                  </h4>
                </div>
                <TipTap
                  text={welcomePageMeta.secondSection?.description}
                  onUpdate={(html) =>
                    intentWelcomePageMeta((prev) => {
                      prev.secondSection!.description = html;
                      return prev;
                    }, "secondSection")
                  }
                />
              </div>

              <hr className="mt-2" />

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl text-gray-700">Testimony Section</h3>
                  <span>/</span>
                  <h4 className="text-xl font-light text-gray-600">Title</h4>
                </div>
                <TipTap
                  text={welcomePageMeta.testimonySection?.title}
                  onUpdate={(html) =>
                    intentWelcomePageMeta((prev) => {
                      prev.testimonySection!.title = html;
                      return prev;
                    }, "testimonySection")
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl text-gray-700">Testimony Section</h3>
                  <span>/</span>
                  <h4 className="text-xl font-light text-gray-600">
                    Description
                  </h4>
                </div>
                <TipTap
                  text={welcomePageMeta.testimonySection?.description}
                  onUpdate={(html) =>
                    intentWelcomePageMeta((prev) => {
                      prev.testimonySection!.description = html;
                      return prev;
                    }, "testimonySection")
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl text-gray-700">Testimony Section</h3>
                  <span>/</span>
                  <h4 className="text-xl font-light text-gray-600">Items</h4>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-2">
                  {welcomePageMeta.testimonySectionItems?.map((el, i) => (
                    <div
                      key={i}
                      className="border border-gray-400 w-full flex flex-col gap-3 p-2"
                    >
                      <div>
                        <h4 className="text-gray-600">Profile Picture</h4>
                        <GalleryPicker
                          postImageUrl="/user/image"
                          onSelect={(url) =>
                            intentWelcomePageMeta((prev) => {
                              prev.testimonySectionItems![i].image = url;
                              return prev;
                            }, "testimonySectionItems")
                          }
                        >
                          {(reference, referenceProp) => (
                            <div
                              ref={reference}
                              {...referenceProp}
                              className="rounded-full w-16 h-16 group relative overflow-hidden cursor-pointer"
                            >
                              <div className="absolute top-0 left-0 bg-black w-full h-full opacity-0 group-hover:opacity-40 grid place-content-center">
                                <i className="fas fa-pen text-white"></i>
                              </div>
                              <img
                                src={
                                  el.image ? resolveImgUrl(el.image) : userImg
                                }
                                className="w-full h-full rounded-full object-cover"
                                alt=""
                              />
                            </div>
                          )}
                        </GalleryPicker>
                      </div>
                      <div>
                        <Input
                          label="Full Name"
                          value={el.name}
                          onChange={(e) =>
                            intentWelcomePageMeta((prev) => {
                              prev.testimonySectionItems![i].name =
                                e.target.value;
                              return prev;
                            }, "testimonySectionItems")
                          }
                        />
                      </div>
                      <div>
                        <Input
                          label="User Name"
                          icon={<i className="fa-solid fa-at"></i>}
                          value={el.username}
                          onChange={(e) =>
                            intentWelcomePageMeta((prev) => {
                              prev.testimonySectionItems![i].username =
                                e.target.value;
                              return prev;
                            }, "testimonySectionItems")
                          }
                        />
                      </div>
                      <div>
                        <h4 className="text-gray-600">Testimony</h4>
                        <TipTap
                          className="!w-full"
                          text={el.description}
                          onUpdate={(html) =>
                            intentWelcomePageMeta((prev) => {
                              prev.testimonySectionItems![i].description = html;
                              return prev;
                            }, "testimonySectionItems")
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          {/* <div className="absolute top-4 right-4 shadow-sm grid place-content-center text-2xl" title="changes saved">
            { (businessesIsPosting || welcomePageIsPosting) ? 
                <i className="fas fa-cloud-arrow-up text-blue-500 "></i> 
              : <i className="fas fa-cloud text-gray-500"></i>
            }
          </div> */}
        </div>
      </div>

      <BlogsSiteSection/>
      <EventsSiteSection/>
      <JobPostSiteSection/>
    </div>
  );
};

type TipTapProps = {
  text?: string;
  onUpdate: (html: string) => void;
  className?: string;
};
const TipTap = ({ text = "", onUpdate, className }: TipTapProps) => {
  const editor = useEditor({
    extensions: [Document, Paragraph, Text, History, Highlight],
    content: text,
    onUpdate: ({ editor }) => {
      onUpdate(editor.getHTML());
    },
  });
  return (
    <div className="flex flex-col gap-2">
      <div
        onClick={() => editor?.commands.focus("end")}
        className={
          "cursor-text p-2 border-gray-400 border w-fit rounded-md min-h-[2rem] min-w-[10rem] " +
          className
        }
      >
        <div onClick={(e) => e.stopPropagation()}>
          <EditorContent editor={editor} />
        </div>
      </div>
      {editor?.isFocused && (
        <div className="w-fit bg-blue-200 border-l-2 border-blue-500 px-2 py-1 text-gray-700">
          press ctrl + h to highlight your text
        </div>
      )}
    </div>
  );
};

export default Site;
